import { unhandledRequest } from 'api/request';

export type OneCRMConfig = {
  password?: string;
  url: string;
  jobTemplate: string;
  applicationTemplate: string;
  onboardingTemplate: string;
  idNumber: string;
};

export type FileUploadStatus =
  | 'P' // Pending
  | 'S' // Success
  | 'E' // Error
  | 'N'; // Not found

type OneCRMUserDTO = {
  id: number;
  email: string;
  name: string;
};

export type OneCRMJobCase = {
  id: number;
  manager: OneCRMUserDTO;
  caseId: string;
  caseName: string;
  infoFileUploadStatus: FileUploadStatus;
  notesFileUploadStatus: FileUploadStatus;
  filesUploadStatus: FileUploadStatus;
  dateCreated: string;
};

export type OneCRMSettings = {
  config: OneCRMConfig | null;
};

export async function retryApplicationTransfer(caseId: number) {
  const response = await unhandledRequest.post<void>({
    url: `/integrations/onecrm/applications/${caseId}/retry/`,
  });

  return response.data;
}

export async function loadOneCRMSettings() {
  const response = await unhandledRequest.get<OneCRMSettings>({
    url: '/integrations/onecrm/settings/',
  });

  return response.data.config;
}

export async function saveOneCRMSettings(data: OneCRMConfig) {
  return unhandledRequest.post<void>({
    url: '/integrations/onecrm/settings/',
    data,
  });
}

export async function updateOneCRMSettings(data: OneCRMConfig) {
  return unhandledRequest.patch<void>({
    url: '/integrations/onecrm/settings/',
    data,
  });
}

export async function loadOneCRMJobCase(jobId: number) {
  const response = await unhandledRequest.get<{ case: OneCRMJobCase | null }>({
    url: `/integrations/onecrm/job-case/${jobId}/`,
  });

  return response.data;
}

export async function createOneCRMJobCase({ jobId, userId }: { jobId: number; userId: number }) {
  const response = await unhandledRequest.post<OneCRMJobCase>({
    url: `/integrations/onecrm/job-case/${jobId}/`,
    data: { manager: userId },
  });

  return response.data;
}

export async function createApplicationCases(jobId: number) {
  const response = await unhandledRequest.post<void>({
    url: `/integrations/onecrm/applications/${jobId}/`,
  });

  return response.data;
}

export type IncompleteApplicationTransfer = {
  id: number;
  name: string;
  errorMessage: string;
  transferDate: string;
};

export type OneCRMApplicationCasesTransfer = {
  id: number;
  totalApplications: number;
  totalCompleted: number;
  transferCompleted: string | null;
  transferStarted: string | null;
  transferredBy: OneCRMUserDTO | null;
  success: boolean;
  incompleteApplications: Array<IncompleteApplicationTransfer>;
};

export async function loadApplicationCases(jobId: number) {
  const response = await unhandledRequest.get<OneCRMApplicationCasesTransfer>({
    url: `/integrations/onecrm/applications/${jobId}/`,
  });
  return response.data;
}

export type OneCRMOnboardingCase = {
  transferCompleted: string | null;
  createdBy: OneCRMUserDTO;
  onboardingFileUploadStatus: FileUploadStatus;
};

export async function loadOnboardingCase(applicationId: number) {
  const response = await unhandledRequest.get<{ case: OneCRMOnboardingCase | null }>({
    url: `/integrations/onecrm/onboarding/${applicationId}/`,
  });

  return response.data;
}

export async function createOnboardingCase(applicationId: number) {
  const response = await unhandledRequest.post<void>({
    url: `/integrations/onecrm/onboarding/${applicationId}/`,
  });

  return response.data;
}
